/**
 * SharedRecapPage — the public reading view of a recap letter passed
 * between guests.
 *
 * Strategic role per Craig's brief: this is the FIRST surface where
 * EventDrop crosses from "a thing used during an event" into "a
 * memory object people pass between each other afterwards." It is
 * the recap email's compounding twin — when a guest shares their
 * letter, the recipient lands HERE, not on a marketing landing
 * page.
 *
 * Hard restraint locks (per Craig, verbatim):
 *   • discovering a letter, not opening a marketing landing page
 *   • NO "Create your own" affordance
 *   • NO sticky signup bars
 *   • NO modal interruptions
 *   • NO floating CTAs
 *   • NO referral counters
 *   • the ONLY conversion mark is "Preserved with EventDrop ✦" in
 *     near-invisible serif microtype at the very bottom
 *
 * Visual language mirrors the recap email: cream parchment surface,
 * Georgia serif, sparing antique-gold accents, generous breathing
 * room, hairline dividers.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Palette — locked to the recap-email tokens in memory_recap.py
const PAPER_BG  = '#FAF6EE';
const INK       = '#2A201A';
const INK_MUTED = '#6A5742';
const ACCENT    = '#A8854C';
const HAIR      = 'rgba(60,40,20,0.18)';

const absoluteMedia = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  return `${BACKEND_URL}${u}`;
};

const formatEventDate = (raw) => {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.valueOf())) return '';
    const months = ['January','February','March','April','May','June',
                    'July','August','September','October','November','December'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return ''; }
};


export default function SharedRecapPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [shareState, setShareState] = useState('idle'); // idle | copied | shared
  const sharePinged = useRef(false);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/recap/shared/${token}`).then(r => {
      if (!active) return;
      setState({ loading: false, error: null, data: r.data });
    }).catch(err => {
      if (!active) return;
      const detail = err?.response?.data?.detail || 'Letter not found';
      setState({ loading: false, error: detail, data: null });
    });
    return () => { active = false; };
  }, [token]);

  const pingReshare = async () => {
    if (sharePinged.current) return;
    sharePinged.current = true;
    try { await axios.post(`${API}/recap/shared/${token}/reshare`); } catch { /* ignore */ }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const eventName = state.data?.event_name || 'this night';
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Moments from ${eventName} ✦`,
          text: `A letter from ${eventName} — for someone who was there.`,
          url,
        });
        await pingReshare();
        setShareState('shared');
        return;
      }
    } catch { /* user cancelled — fall through to copy */ }
    try {
      await navigator.clipboard.writeText(url);
      await pingReshare();
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2400);
    } catch { /* clipboard blocked — surface URL inline below */
      setShareState('manual');
    }
  };

  if (state.loading) {
    return (
      <Frame>
        <p style={{ ...italicStyle, color: INK_MUTED, padding: '120px 0', textAlign: 'center' }}>
          unfolding…
        </p>
      </Frame>
    );
  }

  if (state.error || !state.data) {
    return (
      <Frame>
        <div style={{ padding: '88px 8px', textAlign: 'center' }} data-testid="shared-recap-missing">
          <Eyebrow>A letter that has settled</Eyebrow>
          <Title style={{ marginTop: 16 }}>This letter is no longer here</Title>
          <p style={{ ...italicStyle, color: INK_MUTED, marginTop: 14, lineHeight: 1.6 }}>
            The night it belonged to has rested. If someone you trust shared this with you,
            ask them to pass it along again ✦.
          </p>
        </div>
        <Signature />
      </Frame>
    );
  }

  const {
    event_name, event_date, recipient_first_name, host_inscription,
    chapter_name, chapter_png_url, snap_count, wall_highlights,
  } = state.data;
  const dateLine = formatEventDate(event_date);

  return (
    <Frame>
      {/* ─── LETTERHEAD ─────────────────────────────────────────── */}
      <header style={{ textAlign: 'center', padding: '64px 0 28px' }} data-testid="shared-recap-header">
        <div style={{
          fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic',
          fontSize: 13, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: ACCENT, marginBottom: 16,
        }}>
          A letter from ✦
        </div>
        <h1 style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: 'clamp(28px, 5vw, 38px)', fontWeight: 500,
          color: INK, letterSpacing: '-0.01em', lineHeight: 1.15,
          margin: 0,
        }} data-testid="shared-recap-event-name">{event_name}</h1>
        {dateLine && (
          <div style={{ ...italicStyle, fontSize: 14, color: INK_MUTED, marginTop: 10 }}>
            {dateLine}
          </div>
        )}
      </header>

      {/* ─── GREETING ────────────────────────────────────────────── */}
      <section style={letterStyle} data-testid="shared-recap-greeting">
        <p style={{ margin: '0 0 14px' }}>
          {recipient_first_name ? (
            <>This is the letter sent to <span style={{ fontStyle: 'italic' }}>{recipient_first_name}</span>.</>
          ) : (
            <>This is the letter that was sent.</>
          )}
        </p>
        <p style={{ margin: '0 0 14px' }}>
          A few days have passed, and the night has settled. A small keepsake of what unfolded —
          the moments the room kept coming back to.
        </p>
      </section>

      {/* ─── HOST INSCRIPTION ─────────────────────────────────────── */}
      {host_inscription && (
        <section style={{ margin: '28px 0 8px', textAlign: 'center' }} data-testid="shared-recap-inscription">
          <p style={{
            fontFamily: '"Caveat", "Snell Roundhand", Georgia, "Times New Roman", serif',
            fontStyle: 'italic', fontSize: 'clamp(22px, 3.6vw, 28px)',
            color: INK, lineHeight: 1.4, letterSpacing: '0.005em',
            maxWidth: 520, margin: '0 auto',
          }}>
            — {host_inscription} —
          </p>
          <div style={{
            height: 1, width: 64, background: ACCENT, opacity: 0.42,
            margin: '18px auto 0',
          }} />
        </section>
      )}

      {/* ─── WALL HIGHLIGHTS STRIP ─────────────────────────────────── */}
      {wall_highlights && wall_highlights.length > 0 && (
        <section style={{ padding: '36px 0 16px' }} data-testid="shared-recap-wall">
          <SectionTitle eyebrow="From the wall" subtitle="A handful of moments the room kept coming back to.">
            What unfolded ✦
          </SectionTitle>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 22, marginTop: 24,
          }}>
            {wall_highlights.slice(0, 6).map((s) => (
              <Polaroid key={s.snap_id} snap={s} />
            ))}
          </div>
        </section>
      )}

      {/* ─── CHAPTER PNG ───────────────────────────────────────────── */}
      {chapter_png_url && (
        <section style={{ padding: '36px 0 24px' }} data-testid="shared-recap-chapter">
          <SectionTitle eyebrow="The chapter" subtitle="A printable keepsake — composed slowly.">
            {chapter_name || 'A Memory Chapter ✦'}
          </SectionTitle>
          <div style={{
            marginTop: 24, borderRadius: 4, overflow: 'hidden',
            border: `1px solid ${HAIR}`,
            boxShadow: '0 4px 18px rgba(40,28,14,0.10)',
          }}>
            <img
              src={absoluteMedia(chapter_png_url)}
              alt={chapter_name || 'Memory chapter'}
              style={{ display: 'block', width: '100%' }}
              data-testid="shared-recap-chapter-image"
            />
          </div>
        </section>
      )}

      {/* ─── SNAPS-PRESERVED COUNTER (quiet, never social) ─────────── */}
      <section style={{ padding: '24px 0', textAlign: 'center' }}>
        <div style={{
          ...italicStyle, fontSize: 12.5, color: INK_MUTED,
          letterSpacing: '0.18em', textTransform: 'uppercase',
        }} data-testid="shared-recap-counter">
          ✦ {snap_count} {snap_count === 1 ? 'moment' : 'moments'} preserved ✦
        </div>
      </section>

      {/* ─── PASS-IT-ON ────────────────────────────────────────────── */}
      <section style={{ padding: '28px 0 14px', textAlign: 'center' }} data-testid="shared-recap-passon">
        <Hairline />
        <div style={{ paddingTop: 28 }}>
          <p style={{ ...italicStyle, fontSize: 15, color: INK, margin: 0, lineHeight: 1.6 }}>
            If you were there too, you can keep this.
          </p>
          <button
            type="button"
            onClick={handleShare}
            data-testid="shared-recap-passon-btn"
            style={{
              marginTop: 18, background: 'transparent', border: 'none',
              color: ACCENT, cursor: 'pointer',
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic', fontSize: 16,
              textDecoration: 'underline', textDecorationColor: HAIR,
              textUnderlineOffset: 4, padding: '6px 10px',
            }}
          >
            Pass this letter along
            <span style={{ marginLeft: 6 }}>→</span>
          </button>
          {shareState === 'copied' && (
            <p style={{ ...italicStyle, fontSize: 12.5, color: INK_MUTED, marginTop: 10 }} data-testid="shared-recap-copied">
              The link has been quietly copied to your clipboard ✦
            </p>
          )}
          {shareState === 'shared' && (
            <p style={{ ...italicStyle, fontSize: 12.5, color: INK_MUTED, marginTop: 10 }} data-testid="shared-recap-shared">
              Passed along ✦
            </p>
          )}
          {shareState === 'manual' && (
            <p style={{ ...italicStyle, fontSize: 12, color: INK_MUTED, marginTop: 10, wordBreak: 'break-all' }}>
              {typeof window !== 'undefined' ? window.location.href : ''}
            </p>
          )}
        </div>
      </section>

      <Signature />
    </Frame>
  );
}


// ── Inline component primitives ───────────────────────────────────
const Frame = ({ children }) => (
  <div style={{
    background: PAPER_BG, color: INK, minHeight: '100vh',
    fontFamily: 'Georgia, "Times New Roman", serif',
  }} data-testid="shared-recap-frame">
    <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@500&display=swap" rel="stylesheet" />
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '18px clamp(20px, 6vw, 28px) 0' }}>
      {children}
    </div>
  </div>
);

const Eyebrow = ({ children }) => (
  <div style={{
    fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic',
    fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
    color: ACCENT,
  }}>{children}</div>
);

const Title = ({ children, style }) => (
  <h2 style={{
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: 'clamp(22px, 3.6vw, 28px)', fontWeight: 500,
    color: INK, letterSpacing: '-0.005em', lineHeight: 1.18,
    margin: 0, ...style,
  }}>{children}</h2>
);

const SectionTitle = ({ eyebrow, subtitle, children }) => (
  <div style={{ paddingTop: 16 }}>
    <Hairline />
    <div style={{ padding: '20px 0 0' }}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <Title style={{ marginTop: 6 }}>{children}</Title>
      {subtitle && (
        <p style={{ ...italicStyle, fontSize: 14, color: INK_MUTED, marginTop: 8, lineHeight: 1.55 }}>
          {subtitle}
        </p>
      )}
    </div>
  </div>
);

const Hairline = () => (
  <div style={{ height: 1, background: HAIR, margin: '0 4px' }} />
);

const Polaroid = ({ snap }) => {
  const pen = (snap.pen_color || 'ink').toLowerCase();
  const pen_rgb = (() => {
    if (snap.pen_hex) return snap.pen_hex;
    if (pen === 'gold') return '#9C7224';
    if (pen === 'amber') return '#A85C24';
    if (pen === 'blue') return '#243B6A';
    if (pen === 'rose') return '#9C2C4C';
    return '#1A1A1A';
  })();
  return (
    <div style={{
      background: '#FFFDF6', padding: 8, borderRadius: 2,
      boxShadow: '0 3px 10px rgba(40,28,14,0.18), 0 1px 2px rgba(40,28,14,0.12)',
      transform: `rotate(${(snap.snap_id?.charCodeAt(0) || 0) % 6 - 3}deg)`,
    }}>
      <img
        src={absoluteMedia(snap.photo_url)}
        alt=""
        style={{ display: 'block', width: '100%', height: 180, objectFit: 'cover' }}
      />
      {snap.message && (
        <p style={{
          margin: '8px 4px 4px', textAlign: 'center',
          fontFamily: '"Caveat", "Snell Roundhand", Georgia, cursive',
          fontStyle: 'italic', fontSize: 18, color: pen_rgb,
          lineHeight: 1.2, minHeight: 22,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{snap.message.slice(0, 36)}</p>
      )}
    </div>
  );
};

const Signature = () => (
  /* THE PRINTMAKER'S MARK — the only conversion affordance on this
     page, per Craig's verbatim spec. 3-4px tall, near-invisible
     ivory microtype at the very bottom. NOT a watermark, NOT a CTA,
     just a printmaker's signature. */
  <footer style={{ textAlign: 'center', padding: '40px 0 56px' }} data-testid="shared-recap-signature">
    <Hairline />
    <div style={{ paddingTop: 22 }}>
      <span style={{
        fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic',
        fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
        color: 'rgba(168,133,76,0.65)',
      }}>
        Preserved with <a href="/" style={{
          color: 'rgba(168,133,76,0.85)', textDecoration: 'none',
          borderBottom: '1px solid rgba(168,133,76,0.32)',
        }}>EventDrop</a> ✦
      </span>
    </div>
  </footer>
);

const italicStyle = {
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontStyle: 'italic',
};

const letterStyle = {
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontSize: 16, lineHeight: 1.65, color: INK,
  padding: '8px 4px 12px',
};
